import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { HiOutlineSearch, HiChevronDown, HiChevronUp } from "react-icons/hi";
import { MdClear } from "react-icons/md";
import Button from "../Utilities/Button";
import PaginationNew from "../Utilities/PaginationNew";
import Modal from "../Utilities/Modal";
import Toast from "../Utilities/Toast";
import RightPanelLayout from "../Layout/RightPanelLayout";
import StatusBadge from "./StatusBadge";
import {
  fetchCustomizedOrders,
  fetchCustomizedOrderDetail,
  updateCustomizedOrderStatus,
  setFilter,
  setPage,
  resetDetail,
  selectCustomizedOrder,
} from "../../store/slice/manager/customizedOrder";
import styles from "./style/KeystoneManagement.module.scss";

const LIMIT = 10;

// customized_orders.status enum → badge（中控可在詳情逐步推進狀態）
const STATUS_META = {
  pending: { tone: "off", label: "待簽約" },
  in_progress: { tone: "pending", label: "簽約中" },
  signed: { tone: "trial", label: "簽約完成" },
  complete: { tone: "on", label: "已付款" },
};

// customized_orders.payment_status → badge（start-one 企業方案線上付款；與簽約狀態分開，付款不自動開通）。
// null＝付款功能上線前送出的無金流委託單。
const PAYMENT_META = {
  unpaid: { tone: "off", label: "未付款" },
  pending: { tone: "pending", label: "待轉帳" },
  paid: { tone: "on", label: "已付款" },
  failed: { tone: "off", label: "付款失敗" },
  expired: { tone: "off", label: "已逾期" },
};
const NO_PAYMENT_META = { tone: "trial", label: "無金流委託" };
const paymentMetaOf = (row) =>
  (row?.payment_status && PAYMENT_META[row.payment_status]) || NO_PAYMENT_META;

const PAYMENT_TYPE_LABEL = {
  credit: "中國信託信用卡",
  atm: "ATM 虛擬帳號（綠界）",
};

// 發票設定摘要（付款頁填的；未填統編/載具＝電子發票寄聯絡 Email）
const invoiceText = (row) => {
  if (row?.invoice_tax_id) {
    return `統一編號 ${row.invoice_tax_id}${row.invoice_title ? `（${row.invoice_title}）` : ""}`;
  }
  if (row?.carrier_code) return `手機條碼載具 ${row.carrier_code}`;
  return `電子發票（寄至 ${show(row?.buyer_email || row?.contact_email)}）`;
};

// 詳情內可切換的簽約狀態選項（順序即簽約流程；沿用 STATUS_META 的標籤）
const STATUS_OPTIONS = Object.entries(STATUS_META).map(([value, meta]) => ({
  value,
  label: meta.label,
}));

// 列表工具列的狀態篩選（比照 PersonalSubscriptionList 的 STATUS_FILTER_OPTIONS）
const STATUS_FILTER_OPTIONS = [
  { value: "", label: "全部狀態" },
  ...STATUS_OPTIONS,
];

const splitDateTime = (value) => {
  if (!value) {
    return { date: "", time: "" };
  }
  const [date, time = ""] = String(value).split(" ");
  return { date, time };
};

// 缺值時統一顯示「—」
const show = (value) =>
  value === null || value === undefined || value === "" ? "—" : value;

const formatMoney = (n) => `NT$${(Number(n) || 0).toLocaleString("en-US")}`;

// 公司名稱優先 company_name，缺值 fallback campaign_name
const pickCompany = (row) => row?.company_name ?? row?.campaign_name ?? "";

// 每筆明細顯示自己的單位；加總時才把月費 ×12 換算成年費
const isMonth = (line) => (line?.period_unit ?? "month") === "month";
const unitSuffix = (line) => (isMonth(line) ? "/ 月" : "/ 年");
const annualPrice = (line, { perSeat = false } = {}) => {
  const price = Number(line?.price) || 0;
  // number_of_person 缺值（單一價格方案無人數選擇）預設 1；顯式 0（AI 明細可為 0）需保留為 0，不可 fallback。
  const rawSeats = line?.number_of_person;
  const seats =
    rawSeats === null || rawSeats === undefined || rawSeats === ""
      ? 1
      : Number(rawSeats) || 0;
  const annual = isMonth(line) ? price * 12 : price;
  return perSeat ? annual * seats : annual;
};
const annualSum = (items, options) =>
  (Array.isArray(items) ? items : []).reduce(
    (sum, line) => sum + annualPrice(line, options),
    0
  );

// 單筆「單價 × 人數」小計（不含 ×12）：月費／年費項目分開加總，才不會把已經是年額的
// 年費項目誤跟月費原始單價加在一起再 ×12（實務上模組跟 AI 席位目前都是月費，這裡仍保留
// 年費分支以求正確，混到年費時退回顯示總額、不勉強套用單一公式）。
const rawLineTotal = (line) => {
  const price = Number(line?.price) || 0;
  const rawSeats = line?.number_of_person;
  const seats =
    rawSeats === null || rawSeats === undefined || rawSeats === ""
      ? 1
      : Number(rawSeats) || 0;
  return price * seats;
};
const monthlySumOf = (items) =>
  (Array.isArray(items) ? items : [])
    .filter((line) => isMonth(line))
    .reduce((sum, line) => sum + rawLineTotal(line), 0);
const yearSumOf = (items) =>
  (Array.isArray(items) ? items : [])
    .filter((line) => !isMonth(line))
    .reduce((sum, line) => sum + rawLineTotal(line), 0);

// 單筆明細的顯示文字：多席位（人數 ≠ 1，例如 AI 分析常見多人）要秀出「單價 × 人數 = 小計」，
// 不然使用者看不出這行實際佔多少總額；單一席位（1 或缺值）維持原本單價顯示即可。
// ×12 換算年費只在最下面的應付總額統一算一次（見 totalFormulaText），這裡不重複。
const lineValueText = (line) => {
  const price = Number(line?.price) || 0;
  const rawSeats = line?.number_of_person;
  const seats =
    rawSeats === null || rawSeats === undefined || rawSeats === ""
      ? 1
      : Number(rawSeats) || 0;

  if (seats === 1) {
    return `${formatMoney(price)} ${unitSuffix(line)}`;
  }
  return `${formatMoney(price)} ${unitSuffix(line)} × ${seats} 席 = ${formatMoney(price * seats)} ${unitSuffix(line)}`;
};

// 比照個人訂閱管理「單月 × 12 個月 = 年費」的呈現：月費小計統一乘一次 12，不逐筆重複算式。
const totalFormulaText = (monthlySum, yearSum, total) => {
  if (monthlySum > 0 && yearSum > 0) {
    return `${formatMoney(monthlySum)} X 12 個月 + ${formatMoney(yearSum)} = ${formatMoney(total)}`;
  }
  if (monthlySum > 0) {
    return `${formatMoney(monthlySum)} X 12 個月 = ${formatMoney(total)}`;
  }
  return formatMoney(total);
};

// 應付總額（年約）＝ 席位模組年費（×席次）＋ AI 年費（×人數）＋ 用量加購年費
// 兩張明細後端皆以 total_price = price × number_of_person 存小計，讀取端需一併套用才會一致；
// 用量加購（addon_details）每企業一份、無 number_of_person，annualPrice 會以 1 計。
const grandTotalOf = (order) =>
  annualSum(order?.details, { perSeat: true }) +
  annualSum(order?.ai_details, { perSeat: true }) +
  annualSum(order?.addon_details);

// 用量加購明細（企業 AI 用量 / Email 用量）：名稱、級距、配額皆為後端下單快照。
// 配額一併列出，中控開通時照此填「企業用量設定」的企業 AI 用量（NT$）／Email 總數量（封）。
const addonLineLabel = (line) => {
  const quota = (Number(line?.quota) || 0).toLocaleString("en-US");
  const quotaText =
    line?.addon_code === "email" ? `${quota} 封／月` : `額度 NT$${quota}／月`;
  return `${show(line?.addon_name)}（${show(line?.level_label)}・${quotaText}）`;
};

// 明細行的方案名稱（缺關聯時退回顯示方案 id）
const planName = (line) =>
  line?.plan?.name || `方案 #${line?.plan_id}`;

const lineLabel = (line) => {
  const name = planName(line);
  const seats = line?.number_of_person;
  if (seats !== null && seats !== undefined && seats !== "") {
    return `${name}（${seats} 席）`;
  }
  return name;
};

const UpgradeTag = () => (
  <span
    className={styles.upgradeTag}
    title="來源帳號由個人方案升級為企業"
  >
    個人升級
  </span>
);

const normalizeEmail = (value) => String(value ?? "").trim().toLowerCase();

const isEmailLike = (value) => String(value ?? "").trim().includes("@");

/**
 * 填單身份（個人 / 企業 / 未登入）
 * 對齊 customized_orders 表欄位（後端 store 寫入規則）：
 * - 企業：company_id 有值
 * - 未登入：company_id 為空且 account === contact_email
 * - 個人：company_id 為空、account 為 email 且 ≠ contact_email
 *
 * 相容舊版 seeder／歷史資料：account 曾存「公司名稱」而非 email（無 @），
 * 這類資料應視為企業送單，不能因 company_id 為空就全標成未登入。
 */
export const resolveSubmitterIdentity = (row) => {
  const fromApi = row?.submitter_identity;
  if (
    fromApi === "guest" ||
    fromApi === "personal" ||
    fromApi === "enterprise" ||
    fromApi === "enterprise_upgrade"
  ) {
    return fromApi;
  }

  const companyId = row?.company_id;
  const hasCompany =
    companyId !== null && companyId !== undefined && companyId !== "";

  if (hasCompany) {
    return row?.upgraded_from_personal ? "enterprise_upgrade" : "enterprise";
  }

  const accountRaw = String(row?.account ?? "").trim();
  const account = normalizeEmail(accountRaw);
  const contactEmail = normalizeEmail(row?.contact_email);

  if (account && contactEmail && account === contactEmail) {
    return "guest";
  }

  if (isEmailLike(accountRaw) && account && contactEmail && account !== contactEmail) {
    return "personal";
  }

  if (accountRaw && !isEmailLike(accountRaw)) {
    return row?.upgraded_from_personal ? "enterprise_upgrade" : "enterprise";
  }

  return "guest";
};

export const showLoginAccount = (row) => {
  const accountRaw = String(row?.account ?? "").trim();
  if (!accountRaw || resolveSubmitterIdentity(row) === "guest") {
    return "—";
  }
  if (!isEmailLike(accountRaw)) {
    return "—";
  }
  return accountRaw;
};

const SUBMITTER_IDENTITY_META = {
  guest: {
    tone: "off",
    label: "未登入",
    title: "此委託單於未登入狀態送出",
  },
  personal: {
    tone: "trial",
    label: "個人",
    title: "已登入個人帳號送出（尚未綁定企業）",
  },
  enterprise: {
    tone: "on",
    label: "企業",
    title: "已登入企業帳號送出",
  },
  enterprise_upgrade: {
    tone: "on",
    label: "企業",
    title: "已登入企業帳號送出（來源帳號由個人升級）",
  },
};

const SubmitterIdentityCell = ({ row }) => {
  const identity = resolveSubmitterIdentity(row);
  const meta = SUBMITTER_IDENTITY_META[identity] || SUBMITTER_IDENTITY_META.guest;
  return (
    <span className={styles.identityCell} title={meta.title}>
      <StatusBadge tone={meta.tone} label={meta.label} />
      {identity === "enterprise_upgrade" ? <UpgradeTag /> : null}
    </span>
  );
};

const EnterprisePurchaseList = () => {
  const dispatch = useDispatch();
  const { rows, total, page, loading, filter, detail, detailLoading } =
    useSelector(selectCustomizedOrder);

  // search 文字框用 local draft（避免每打一個字都打 API），送出才套用
  const [draftSearch, setDraftSearch] = useState(filter.search || "");
  const [detailOpen, setDetailOpen] = useState(false);
  // 簽約狀態編輯：draft 隨詳情同步，按「更新狀態」才送出
  const [statusDraft, setStatusDraft] = useState("");
  const [savingStatus, setSavingStatus] = useState(false);
  const [toast, setToast] = useState({ show: false, title: "", error: false });

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(total / LIMIT)),
    [total]
  );

  // 詳情載入／更新後，把狀態下拉還原成目前值
  useEffect(() => {
    setStatusDraft(detail?.status ?? "");
  }, [detail?.id, detail?.status]);

  useEffect(() => {
    if (!toast.show) return;
    const t = setTimeout(() => setToast((p) => ({ ...p, show: false })), 2500);
    return () => clearTimeout(t);
  }, [toast.show]);

  const load = useCallback(() => {
    dispatch(fetchCustomizedOrders({ filter, page, limit: LIMIT }));
  }, [dispatch, filter, page]);

  // 列表：進頁 / 搜尋 / 換頁 / 排序時重新取得
  useEffect(() => {
    load();
  }, [load]);

  const applySearch = () => {
    dispatch(setFilter({ search: draftSearch.trim() }));
  };

  const clearSearch = () => {
    setDraftSearch("");
    dispatch(setFilter({ search: "" }));
  };

  // 送出時間排序：點表頭切換 由新到舊(desc) ⇆ 由舊到新(asc)
  const sortDir = filter.sort_dir || "desc";
  const toggleSort = () => {
    dispatch(setFilter({ sort_dir: sortDir === "desc" ? "asc" : "desc" }));
  };

  const handlePageChange = (event) => {
    const next =
      event && typeof event === "object" && "selected" in event
        ? event.selected + 1
        : Number(event);
    if (Number.isInteger(next) && next > 0) {
      dispatch(setPage(next));
    }
  };

  const handleOpenDetail = (row) => {
    setDetailOpen(true);
    dispatch(fetchCustomizedOrderDetail(row.id));
  };

  const handleCloseDetail = () => {
    setDetailOpen(false);
    dispatch(resetDetail());
  };

  // 更新簽約狀態（pending → in_progress → signed → complete，中控可自由調整）
  const handleUpdateStatus = async () => {
    if (!detail || savingStatus || !statusDraft || statusDraft === detail.status) {
      return;
    }
    setSavingStatus(true);
    try {
      await dispatch(
        updateCustomizedOrderStatus({ id: detail.id, status: statusDraft })
      ).unwrap();
      setToast({ show: true, title: "已更新簽約狀態", error: false });
    } catch (e) {
      setToast({ show: true, title: "更新失敗，請稍後再試", error: true });
    } finally {
      setSavingStatus(false);
    }
  };

  // 明細金額（全部年費）：上區塊=席位模組(details)、下區塊=AI(ai_details)
  const seatRows = Array.isArray(detail?.details) ? detail.details : [];
  const aiRows = Array.isArray(detail?.ai_details) ? detail.ai_details : [];
  const addonRows = Array.isArray(detail?.addon_details)
    ? detail.addon_details
    : [];
  const allDetailRows = [...seatRows, ...aiRows, ...addonRows];
  const monthlySum = monthlySumOf(allDetailRows); // 月費項目小計（不含 ×12）
  const yearSum = yearSumOf(allDetailRows); // 年費項目小計（本身已是年額）
  const detailGrand = grandTotalOf(detail); // 應付總額（年約）
  const hasBreakdown = allDetailRows.length > 0;
  const statusMeta = detail
    ? STATUS_META[detail.status] || STATUS_META.pending
    : STATUS_META.pending;

  return (
    <RightPanelLayout title="企業購買委託單">
      <div className={styles.listToolbar}>
        <div className={styles.listToolbarTitle}>委託單列表</div>
        <div className={styles.toolbarActions}>
          <select
            className={styles.select}
            value={filter.status}
            onChange={(e) => dispatch(setFilter({ status: e.target.value }))}
            aria-label="簽約狀態篩選"
          >
            {STATUS_FILTER_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          <div className={styles.searchBar}>
            <button
              type="button"
              className={styles.searchBarIconBtn}
              onClick={applySearch}
              aria-label="搜尋"
            >
              <HiOutlineSearch className={styles.searchBarIcon} />
            </button>
            <input
              type="search"
              className={styles.searchBarInput}
              value={draftSearch}
              placeholder="搜尋公司名稱、統編或聯絡人"
              onChange={(e) => setDraftSearch(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  applySearch();
                }
              }}
              aria-label="搜尋公司名稱、統編或聯絡人"
            />
            {draftSearch ? (
              <button
                type="button"
                className={styles.searchBarClear}
                onClick={clearSearch}
                aria-label="清除搜尋"
              >
                <MdClear className={styles.searchBarClearIcon} />
              </button>
            ) : null}
          </div>
          <Button type="primary" title="搜尋" onClick={applySearch} />
        </div>
      </div>

      {total > 0 ? (
        <div className={styles.paginationTop}>
          <PaginationNew
            onPageChange={handlePageChange}
            pageCount={page}
            limit={LIMIT}
            totalPages={totalPages}
            totalData={total}
          />
        </div>
      ) : null}

      <div className={styles.tableContainer}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>
                <button
                  type="button"
                  className={styles.sortHeader}
                  onClick={toggleSort}
                  aria-label={
                    sortDir === "desc"
                      ? "送出時間：目前由新到舊，點擊改由舊到新"
                      : "送出時間：目前由舊到新，點擊改由新到舊"
                  }
                >
                  送出時間
                  {sortDir === "desc" ? (
                    <HiChevronDown className={styles.sortIcon} />
                  ) : (
                    <HiChevronUp className={styles.sortIcon} />
                  )}
                </button>
              </th>
              <th>填單身份</th>
              <th>公司名稱</th>
              <th>統一編號</th>
              <th>聯絡人</th>
              <th>聯絡 Email</th>
              <th>應付總額</th>
              <th>簽約狀態</th>
              <th>付款狀態</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td className={styles.tableEmptyCell} colSpan={10}>
                  載入中…
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td className={styles.tableEmptyCell} colSpan={10}>
                  尚無委託資料
                </td>
              </tr>
            ) : (
              rows.map((row) => {
                const { date, time } = splitDateTime(row.created_at);
                const meta = STATUS_META[row.status] || STATUS_META.pending;
                return (
                  <tr key={row.id}>
                    <td>
                      <div className={styles.cellStrong}>{date}</div>
                      <div className={styles.cellSub}>{time}</div>
                    </td>
                    <td>
                      <SubmitterIdentityCell row={row} />
                    </td>
                    <td className={styles.cellStrong}>
                      <span className={styles.companyCell}>
                        {show(pickCompany(row))}
                      </span>
                    </td>
                    <td>{show(row.tax_id)}</td>
                    <td>
                      {show(row.contact)}
                      <div className={styles.cellSub}>
                        {show(row.contact_position)}
                      </div>
                    </td>
                    <td>{show(row.contact_email)}</td>
                    <td className={styles.cellStrong}>
                      {formatMoney(grandTotalOf(row))}
                    </td>
                    <td>
                      <StatusBadge tone={meta.tone} label={meta.label} />
                    </td>
                    <td>
                      <StatusBadge
                        tone={paymentMetaOf(row).tone}
                        label={paymentMetaOf(row).label}
                      />
                    </td>
                    <td>
                      <button
                        type="button"
                        className={styles.moreBtn}
                        onClick={() => handleOpenDetail(row)}
                      >
                        更多資訊
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <Modal
        isOpen={detailOpen}
        size="md"
        width="min(600px, calc(100vw - 32px))"
      >
        {detailOpen ? (
          <div className={styles.modalRoot}>
            <div className={styles.modalHead}>
              <div>
                <div className={styles.modalTitleRow}>
                  <h3 className={styles.modalTitle}>
                    {show(pickCompany(detail))}
                  </h3>
                  <SubmitterIdentityCell row={detail} />
                </div>
                <p className={styles.modalSub}>
                  送出時間 {show(detail?.created_at)}
                </p>
              </div>
              <button
                type="button"
                className={styles.modalClose}
                onClick={handleCloseDetail}
                aria-label="關閉"
              >
                ✕
              </button>
            </div>

            <div className={styles.modalBody}>
              {detailLoading && !detail ? (
                <div className={styles.tableEmptyCell}>載入中…</div>
              ) : (
                <>
                  <div className={styles.dividerLabel}>
                    帳號資料
                    <span className={styles.dividerHint}>
                      用於簽約與帳號開通聯繫
                    </span>
                  </div>
                  <div className={styles.infoGrid}>
                    <div>
                      <div className={styles.infoKey}>填單身份</div>
                      <div className={styles.infoVal}>
                        <SubmitterIdentityCell row={detail} />
                      </div>
                    </div>
                    <div>
                      <div className={styles.infoKey}>登入帳號</div>
                      <div className={styles.infoVal}>
                        {showLoginAccount(detail)}
                      </div>
                    </div>
                    <div>
                      <div className={styles.infoKey}>公司名稱</div>
                      <div className={styles.infoVal}>
                        {show(pickCompany(detail))}
                      </div>
                    </div>
                    <div>
                      <div className={styles.infoKey}>公司統一編號</div>
                      <div className={styles.infoVal}>
                        {show(detail?.tax_id)}
                      </div>
                    </div>
                    <div>
                      <div className={styles.infoKey}>聯絡人姓名</div>
                      <div className={styles.infoVal}>
                        {show(detail?.contact)}
                      </div>
                    </div>
                    <div>
                      <div className={styles.infoKey}>聯絡人職稱</div>
                      <div className={styles.infoVal}>
                        {show(detail?.contact_position)}
                      </div>
                    </div>
                    <div>
                      <div className={styles.infoKey}>聯絡 Email</div>
                      <div className={styles.infoVal}>
                        {show(detail?.contact_email)}
                      </div>
                    </div>
                    <div>
                      <div className={styles.infoKey}>聯絡電話</div>
                      <div className={styles.infoVal}>
                        {show(detail?.contact_phone)}
                      </div>
                    </div>
                  </div>

                  {detail?.payment_status ? (
                    <>
                      <div className={styles.dividerLabel}>
                        付款資訊
                        <span className={styles.dividerHint}>
                          付款成功不會自動開通，請確認已付款後再推進簽約狀態並開通
                        </span>
                      </div>
                      <div className={styles.infoGrid}>
                        <div>
                          <div className={styles.infoKey}>付款狀態</div>
                          <div className={styles.infoVal}>
                            <StatusBadge
                              tone={paymentMetaOf(detail).tone}
                              label={paymentMetaOf(detail).label}
                            />
                          </div>
                        </div>
                        <div>
                          <div className={styles.infoKey}>付款方式</div>
                          <div className={styles.infoVal}>
                            {show(PAYMENT_TYPE_LABEL[detail.payment_type])}
                          </div>
                        </div>
                        <div>
                          <div className={styles.infoKey}>金流訂單編號</div>
                          <div className={styles.infoVal}>
                            {show(detail.order_number)}
                          </div>
                        </div>
                        <div>
                          <div className={styles.infoKey}>付款時間</div>
                          <div className={styles.infoVal}>
                            {show(detail.paid_at)}
                          </div>
                        </div>
                        {detail.payment_type === "atm" ? (
                          <>
                            <div>
                              <div className={styles.infoKey}>ATM 虛擬帳號</div>
                              <div className={styles.infoVal}>
                                {detail.atm_v_account
                                  ? `(${detail.atm_bank_code}) ${detail.atm_v_account}`
                                  : "—"}
                              </div>
                            </div>
                            <div>
                              <div className={styles.infoKey}>繳費期限</div>
                              <div className={styles.infoVal}>
                                {show(detail.payment_expired_at)}
                              </div>
                            </div>
                          </>
                        ) : null}
                        <div>
                          <div className={styles.infoKey}>發票</div>
                          <div className={styles.infoVal}>
                            {invoiceText(detail)}
                          </div>
                        </div>
                      </div>
                    </>
                  ) : null}

                  <div className={styles.dividerLabel}>年約金額試算</div>
                  <div className={styles.breakdownBox}>
                    {/* 上區塊：席位模組明細（customized_order_details，顯示各自單位） */}
                    {seatRows.map((line) => (
                      <div key={`d-${line.id}`} className={styles.breakdownRow}>
                        <span className={styles.breakdownLabel}>
                          {lineLabel(line)}
                        </span>
                        <span className={styles.breakdownValue}>
                          {lineValueText(line)}
                        </span>
                      </div>
                    ))}
                    {/* 下區塊：AI 明細（customized_order_ai_details，顯示各自單位） */}
                    {aiRows.map((line) => (
                      <div key={`a-${line.id}`} className={styles.breakdownRow}>
                        <span className={styles.breakdownLabel}>
                          {lineLabel(line)}
                        </span>
                        <span className={styles.breakdownValue}>
                          {lineValueText(line)}
                        </span>
                      </div>
                    ))}
                    {/* 用量加購（customized_order_addon_details：企業 AI 用量 / Email 用量） */}
                    {addonRows.map((line) => (
                      <div key={`u-${line.id}`} className={styles.breakdownRow}>
                        <span className={styles.breakdownLabel}>
                          {addonLineLabel(line)}
                        </span>
                        <span className={styles.breakdownValue}>
                          {lineValueText(line)}
                        </span>
                      </div>
                    ))}
                    {!hasBreakdown ? (
                      <div className={styles.breakdownRow}>
                        <span className={styles.breakdownLabel}>
                          尚無金額明細
                        </span>
                        <span className={styles.breakdownValue}>—</span>
                      </div>
                    ) : null}
                    {/* 比照個人訂閱管理：上面逐筆列完後，只在這裡統一加總 ×12 一次，不逐筆重複算式 */}
                    <div className={styles.grandTotalRow}>
                      <span>應付總額（年約）</span>
                      <span className={styles.grandTotalValue}>
                        {totalFormulaText(monthlySum, yearSum, detailGrand)}
                      </span>
                    </div>
                  </div>

                  <div className={styles.contractNote}>
                    <b>合約規則：</b>
                    年約期間僅可增加席位，不可減少；如需減少須等年約期滿方可調整。AI
                    用量額度費率鎖定一年，可個別升級，補差價以當月剩餘天數比例計算。
                  </div>

                  <div className={styles.dividerLabel}>簽約狀態</div>
                  <div className={styles.statusEditRow}>
                    <StatusBadge
                      tone={statusMeta.tone}
                      label={statusMeta.label}
                    />
                    <select
                      className={styles.select}
                      value={statusDraft}
                      onChange={(e) => setStatusDraft(e.target.value)}
                      disabled={savingStatus}
                      aria-label="簽約狀態"
                    >
                      {STATUS_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                    <Button
                      type="primary"
                      title={savingStatus ? "更新中…" : "更新狀態"}
                      onClick={handleUpdateStatus}
                      disabled={
                        savingStatus ||
                        !statusDraft ||
                        statusDraft === detail?.status
                      }
                    />
                  </div>
                </>
              )}
            </div>

            <div className={styles.modalFoot}>
              <button
                type="button"
                className={styles.btnGhost}
                onClick={handleCloseDetail}
              >
                關閉
              </button>
            </div>
          </div>
        ) : null}
      </Modal>

      <Toast show={toast.show} title={toast.title} error={toast.error} />
    </RightPanelLayout>
  );
};

export default EnterprisePurchaseList;
